import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { EntityType, EntitySentiment, Entity, ENTITY_TYPE_LABELS } from '../../../types/annotation';

interface EntityTagModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: { entityType: EntityType; entityName: string; sentiment?: EntitySentiment }) => void;
  initialData?: Partial<Entity>;
  suggestions?: string[];
}

const ENTITY_TYPE_ORDER: EntityType[] = [
  'person',
  'organization',
  'post_designation',
  'government_institution',
  'policy',
  'issue',
  'location',
  'programme_scheme',
  'thing_object',
];

interface SentimentOption {
  value: EntitySentiment;
  label: string;
  activeClass: string;
  inactiveClass: string;
}

const SENTIMENT_OPTIONS: SentimentOption[] = [
  {
    value: 'positive',
    label: 'Positive',
    activeClass: 'bg-green-600 text-white ring-2 ring-green-400',
    inactiveClass: 'bg-green-50 text-green-700 hover:bg-green-100 border border-green-200',
  },
  {
    value: 'negative',
    label: 'Negative',
    activeClass: 'bg-red-600 text-white ring-2 ring-red-400',
    inactiveClass: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200',
  },
  {
    value: 'neutral',
    label: 'Neutral',
    activeClass: 'bg-gray-600 text-white ring-2 ring-gray-400',
    inactiveClass: 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200',
  },
  {
    value: 'mixed',
    label: 'Mixed',
    activeClass: 'bg-amber-500 text-white ring-2 ring-amber-400',
    inactiveClass: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200',
  },
];

const EntityTagModal: React.FC<EntityTagModalProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  suggestions = [],
}) => {
  const [entityType, setEntityType] = useState<EntityType>('person');
  const [entityName, setEntityName] = useState('');
  const [sentiment, setSentiment] = useState<EntitySentiment | undefined>(undefined);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setEntityType((initialData?.entityType as EntityType) ?? 'person');
      setEntityName(initialData?.entityName ?? '');
      setSentiment(initialData?.sentiment);
      setError(null);
      setShowSuggestions(false);
      setTimeout(() => nameInputRef.current?.focus(), 50);
    }
  }, [open, initialData]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const filteredSuggestions = suggestions.filter(
    (s) =>
      entityName.trim().length > 0 &&
      s.toLowerCase().includes(entityName.toLowerCase()) &&
      s !== entityName,
  );

  const handleSave = () => {
    if (!entityName.trim()) {
      setError('Entity name is required.');
      return;
    }
    setError(null);
    onSave({
      entityType,
      entityName: entityName.trim(),
      sentiment,
    });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="Tag entity"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            {initialData?.entityId ? 'Edit Entity' : 'Add Entity'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <div className="px-6 py-4 space-y-4">
          {/* Entity Type */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Entity Type
            </label>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value as EntityType)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white"
            >
              {ENTITY_TYPE_ORDER.map((t) => (
                <option key={t} value={t}>
                  {ENTITY_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {/* Entity Name with autocomplete */}
          <div className="relative">
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Entity Name
            </label>
            <input
              ref={nameInputRef}
              type="text"
              value={entityName}
              onChange={(e) => {
                setEntityName(e.target.value);
                setShowSuggestions(true);
              }}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
              placeholder="Enter entity name..."
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
            />
            {showSuggestions && filteredSuggestions.length > 0 && (
              <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                {filteredSuggestions.slice(0, 8).map((s) => (
                  <li
                    key={s}
                    onMouseDown={() => {
                      setEntityName(s);
                      setShowSuggestions(false);
                    }}
                    className="px-3 py-2 text-sm text-gray-700 hover:bg-violet-50 hover:text-violet-700 cursor-pointer"
                  >
                    {s}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Sentiment */}
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
              Sentiment (optional)
            </label>
            <div className="flex gap-2">
              {SENTIMENT_OPTIONS.map((opt) => {
                const isActive = sentiment === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSentiment(isActive ? undefined : opt.value)}
                    className={[
                      'flex-1 px-2 py-1.5 text-xs rounded-lg font-medium transition-colors',
                      isActive ? opt.activeClass : opt.inactiveClass,
                    ].join(' ')}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-sm rounded-lg font-medium bg-violet-600 text-white hover:bg-violet-700 transition-colors"
          >
            Save Entity
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntityTagModal;
